import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Briefcase, Users, Video, CheckCircle2, Plus } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/dashboard/status-badge";
import { DecisionBadge } from "@/components/interviews/decision";
import { FilterBar } from "@/components/interviews/filter-bar";
import { useDict } from "@/lib/i18n/context";
import type { InterviewStatus } from "@/lib/constants";
import type { User } from "@/lib/types";

type InterviewRow = {
  id: string;
  candidate_name: string | null;
  job_title: string | null;
  total_score: number | null;
  decision: string | null;
  status: InterviewStatus;
};

type DashboardData = {
  user: User;
  counts: {
    jobs: number;
    candidates: number;
    interviews: number;
    completed: number;
  };
  rows: InterviewRow[];
  jobs: { id: string; title: string }[];
  hasFilters: boolean;
};

export function DashboardPage() {
  const t = useDict();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState<DashboardData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const q = searchParams.get("q") || "";
  const status = searchParams.get("status") || "";
  const decision = searchParams.get("decision") || "";
  const job = searchParams.get("job") || "";
  const from = searchParams.get("from") || "";
  const to = searchParams.get("to") || "";
  const qs = searchParams.toString();

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    fetch(`/api/dashboard${qs ? `?${qs}` : ""}`, { credentials: "include" })
      .then(async (res) => {
        if (!res.ok) throw new Error("Failed to load");
        return res.json() as Promise<DashboardData>;
      })
      .then((json) => {
        if (active) setData(json);
      })
      .catch(() => {
        if (active) setError("Failed to load");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [qs]);

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-muted">
        Loading…
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-muted">
        {error || "Failed to load"}
      </div>
    );
  }

  const { user, counts, rows, jobs, hasFilters } = data;
  const stats = [
    { label: t.dashboard.statJobs, value: counts.jobs, icon: Briefcase, href: "/jobs" },
    {
      label: t.dashboard.statCandidates,
      value: counts.candidates,
      icon: Users,
      href: "/candidates",
    },
    {
      label: t.dashboard.statInterviews,
      value: counts.interviews,
      icon: Video,
      href: "/interviews",
    },
    {
      label: t.dashboard.statCompleted,
      value: counts.completed,
      icon: CheckCircle2,
      href: "/interviews",
    },
  ];

  return (
    <div className="mx-auto max-w-6xl min-w-0 space-y-6 sm:space-y-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h1 className="truncate text-xl font-bold sm:text-2xl">
            {t.dashboard.welcome}
            {user.full_name ? ` ${user.full_name.split(" ")[0]}` : ""}
          </h1>
          <p className="text-sm text-muted sm:text-base">{t.dashboard.subtitle}</p>
        </div>
        <div className="grid w-full grid-cols-1 gap-2 sm:flex sm:w-auto sm:gap-3">
          <Link to="/jobs/new" className="min-w-0 sm:inline-flex">
            <Button variant="outline" className="w-full sm:w-auto">
              <Plus className="h-4 w-4 shrink-0" /> {t.dashboard.newJob}
            </Button>
          </Link>
          <Link to="/candidates/new" className="min-w-0 sm:inline-flex">
            <Button className="w-full sm:w-auto">
              <Plus className="h-4 w-4 shrink-0" /> {t.dashboard.inviteCandidate}
            </Button>
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:gap-4 lg:grid-cols-4">
        {stats.map((s) => (
          <Link key={s.label} to={s.href} className="min-w-0">
            <Card className="h-full transition-shadow hover:shadow-md">
              <CardContent className="flex items-center gap-2 pt-4 sm:gap-4 sm:pt-6">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-600 sm:h-12 sm:w-12">
                  <s.icon className="h-5 w-5 sm:h-6 sm:w-6" />
                </span>
                <div className="min-w-0">
                  <p className="text-xl font-bold sm:text-2xl">{s.value}</p>
                  <p className="truncate text-xs text-muted sm:text-sm">
                    {s.label}
                  </p>
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      <Card className="min-w-0 overflow-hidden">
        <CardContent className="pt-6">
          <div className="mb-4 flex min-w-0 items-center justify-between gap-3">
            <h2 className="min-w-0 truncate text-base font-semibold sm:text-lg">
              {hasFilters ? t.dashboard.results : t.dashboard.recentInterviews}
            </h2>
            <Link
              to="/interviews"
              className="shrink-0 text-sm font-medium text-brand-600"
            >
              {t.dashboard.viewAll}
            </Link>
          </div>

          <div className="mb-4">
            <FilterBar
              basePath="/dashboard"
              jobs={jobs}
              initial={{ q, status, decision, job, from, to }}
            />
          </div>

          {rows.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border py-12 text-center">
              <p className="text-muted">
                {hasFilters ? t.dashboard.noMatches : t.dashboard.noInterviews}
              </p>
              {!hasFilters && (
                <Link to="/candidates/new">
                  <Button className="mt-4">{t.dashboard.inviteFirst}</Button>
                </Link>
              )}
            </div>
          ) : (
            <div className="divide-y divide-border">
              {rows.map((i) => (
                <Link
                  key={i.id}
                  to={`/interviews/${i.id}`}
                  className="flex flex-col gap-2 py-3 hover:bg-surface sm:flex-row sm:items-center sm:justify-between sm:gap-4"
                >
                  <div className="min-w-0">
                    <p className="truncate font-medium">
                      {i.candidate_name ?? t.common.candidate}
                    </p>
                    <p className="truncate text-sm text-muted">
                      {i.job_title ?? t.common.generalInterview}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                    {i.total_score != null && (
                      <span className="text-sm font-semibold">
                        {i.total_score}/100
                      </span>
                    )}
                    <DecisionBadge decision={i.decision} />
                    <StatusBadge status={i.status} />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
